// Map API field names to display names for the document
export const CRITERIA_FIELD_MAPPING: Record<string, string> = {
  'Malocclusion type': 'Malocclusion Type',
  'maloclusion': 'Malocclusion Type',
  'All permanent teeth present': 'All Permanent Teeth Present',
  'all_permanent_teeth': 'All Permanent Teeth Present',
  'Crowding on upper anterior region': 'Crowding (Upper)',
  'Crowding on lower anterior region': 'Crowding (Lower)',
  'crowding': 'Crowding',
  'Maxillary transverse constriction in premolar region': 'Maxillary Transverse Constriction (Premolar Region)',
  'maxillary_transverse_constriction': 'Maxillary Transverse Constriction (Premolar Region)',
  'Spee curve on lower arch': 'Spee Curve (Lower Arch)',
  'spee_curve': 'Spee Curve (Lower Arch)',
  'What type of correction is indicated based on the patient profile': 'Correction Type Indicated',
  'correction_type': 'Correction Type Indicated',
  'Upper midline aligned with face midline': 'Upper Midline Alignment',
  'upper_midline': 'Upper Midline Alignment',
  'Distalize more than 1mm': 'Distalization Amount',
  'distalization_amount': 'Distalization Amount',
  'distalization_side': 'Distalization Side',
};

// Get standardized field name
export function getStandardFieldName(apiFieldName: string): string {
  return CRITERIA_FIELD_MAPPING[apiFieldName] || apiFieldName;
}

// Extract criteria value from API response
export function extractCriteriaValue(criteriaData: any): string {
  if (!criteriaData) return 'Not specified';
  
  // Handle different data structures
  if (typeof criteriaData === 'string') {
    return criteriaData;
  }
  
  if (criteriaData.value !== undefined) {
    return criteriaData.value;
  }
  
  return 'Not specified';
}

// Format all case criteria for document display
export function formatCaseCriteria(caseCriteriaResponse: any): Record<string, string> {
  const formatted: Record<string, string> = {
    'Malocclusion Type': 'Not specified',
    'All Permanent Teeth Present': 'Not specified',
    'Crowding': 'Not specified',
    'Maxillary Transverse Constriction (Premolar Region)': 'Not specified',
    'Spee Curve (Lower Arch)': 'Not specified',
    'Correction Type Indicated': 'Not specified',
    'Upper Midline Alignment': 'Not specified',
    'Distalization Amount': 'Not specified',
    'Distalization Side': 'Not applicable',
  };

  if (!caseCriteriaResponse?.case_criteria) {
    return formatted;
  }

  const criteria = caseCriteriaResponse.case_criteria;

  // Process each field from the API response
  Object.entries(criteria).forEach(([key, value]) => {
    // Skip internal fields
    if (key.startsWith('_')) return;
    
    const standardName = getStandardFieldName(key);
    const extractedValue = extractCriteriaValue(value);
    
    // Handle specific field mappings
    if (key === 'Malocclusion type' || key === 'maloclusion') {
      formatted['Malocclusion Type'] = extractedValue;
    } else if (key === 'All permanent teeth present') {
      formatted['All Permanent Teeth Present'] = extractedValue;
    } else if (key.includes('Crowding')) {
      // Combine crowding information
      if (key === 'Crowding on upper anterior region' && extractedValue === 'Yes') {
        formatted['Crowding'] = formatted['Crowding'] === 'Not specified' 
          ? 'Upper anterior' 
          : formatted['Crowding'] + ', Upper anterior';
      } else if (key === 'Crowding on lower anterior region' && extractedValue === 'Yes') {
        formatted['Crowding'] = formatted['Crowding'] === 'Not specified' 
          ? 'Lower anterior' 
          : formatted['Crowding'] + ', Lower anterior';
      } else if (key === 'crowding') {
        formatted['Crowding'] = extractedValue;
      }
    } else if (key.includes('transverse constriction')) {
      formatted['Maxillary Transverse Constriction (Premolar Region)'] = extractedValue;
    } else if (key.includes('Spee curve')) {
      formatted['Spee Curve (Lower Arch)'] = extractedValue;
    } else if (key.includes('correction') && key.includes('indicated')) {
      formatted['Correction Type Indicated'] = extractedValue;
    } else if (key.includes('Upper midline')) {
      formatted['Upper Midline Alignment'] = extractedValue === 'Yes' 
        ? 'Aligned with facial midline' 
        : extractedValue;
    } else if (key.includes('Distalize more than 1mm')) {
      formatted['Distalization Amount'] = extractedValue === 'Yes' 
        ? 'More than 1mm' 
        : extractedValue === 'No' 
        ? 'Less than 1mm' 
        : extractedValue;
    } else if (key === 'distalization_side') {
      formatted['Distalization Side'] = extractedValue || 'Not applicable';
    }
  });

  // Clean up combined crowding field
  if (formatted['Crowding'] === 'Upper anterior, Lower anterior') {
    formatted['Crowding'] = 'Both upper and lower anterior regions';
  }

  return formatted;
}

// Generate a hash for the session ID (first 8 characters)
export function hashSessionId(sessionId: string): string {
  if (!sessionId) return 'N/A';
  
  // For display purposes, show first 8 characters
  if (sessionId.length <= 8) {
    return sessionId;
  }
  
  return sessionId.substring(0, 8) + '...';
}